import {
  updateResourceRequestsCache,
  updateResourcePlanSummaryTabCountCache,
  updateResourcePlansCache
} from '~/modules/resourcing/common/util/resourceRequestUtil.js';
import { updateCache as draftUpdateCache } from '~/modules/resourcing/common/enhancers/requestStatusHandlers/withMarkResourceRequestAsDraft';
import { updateCacheForResourcingPlan } from '~/modules/resourcing/common/hooks/useMarkToBeHiredResourceRequestWithHiringRequestedSubStatus';
import { updateCache as updateDeleteCache } from '~/modules/resourcing/common/hooks/useDeleteResourceRequestHandler';
import { updateHiringStatusSummaryCountCache } from '~/modules/resourcing/common/util/hiringStatusUtil';
import { ResourceRequestStatus } from '~/types';

export const getCacheUpdateFunctions = ({
  request,
  filter,
  hiringStatusSummaryCountQueryVariables,
  resourcePlanTabVariables,
  resourceRequestsQueryVariables,
  isHiringStatusSubFilterCacheUpdateEnabled,
  isProjectResourcingView,
  isPtiEnabled,
  isResourceActualModeEnabled,
  skipProjectTotalsCacheUpdate,
  onResourceAcceptCacheUpdate,
  resourcePlanFilter
}) => () => {
  const summaryCountPlanFilter = resourcePlanFilter;

  if (isHiringStatusSubFilterCacheUpdateEnabled) {
    delete summaryCountPlanFilter?.hiringStatuses;
    delete summaryCountPlanFilter?.excludedHiringStatuses;
  }

  return {
    getDefaultCacheFunctions: ({
      resourceRequest,
      requestStatus,
      responseType
    }) => [
      updateResourceRequestsCache({
        projectId: resourceRequest.projectUri,
        responseType
      }),
      ...(isProjectResourcingView
        ? [
            updateResourcePlanSummaryTabCountCache({
              responseType,
              newRequestStatus: requestStatus,
              resourceRequest,
              planFilter: summaryCountPlanFilter
            })
          ]
        : [])
    ],
    onResourceRequestSubmitCacheFunctions: [
      ...(isProjectResourcingView
        ? [
            updateResourcePlanSummaryTabCountCache({
              responseType: 'markResourceRequestAsSubmitted2',
              newRequestStatus: ResourceRequestStatus.Submitted,
              resourceRequest: request,
              planFilter: summaryCountPlanFilter
            }),
            updateResourcePlansCache({
              existingResourceRequest: request,
              newRequestStatus: ResourceRequestStatus.Submitted,
              requestAccessLevel:
                resourceRequestsQueryVariables?.requestAccessLevel,
              planFilter: resourcePlanFilter,
              chartSelectedTab: resourcePlanTabVariables?.resourcingPlanTab
            })
          ]
        : []),
      ...(isHiringStatusSubFilterCacheUpdateEnabled
        ? [
            updateHiringStatusSummaryCountCache({
              responseType: 'markResourceRequestAsSubmitted2',
              newRequestStatus: ResourceRequestStatus.Submitted,
              oldHiringStatus: request.sourceMetadata?.hiringStatus,
              existingRequestStatus: request.requestStatus,
              hiringStatusSummaryCountQueryVariables
            })
          ]
        : [])
    ],
    onResourceRequestRejectCacheFunctions: [
      ...(isProjectResourcingView
        ? [
            updateResourcePlanSummaryTabCountCache({
              responseType: 'markResourceRequestAsRejected2',
              newRequestStatus: ResourceRequestStatus.Rejected,
              resourceRequest: request,
              planFilter: summaryCountPlanFilter
            }),
            updateResourcePlansCache({
              existingResourceRequest: request,
              newRequestStatus: ResourceRequestStatus.Rejected,
              requestAccessLevel:
                resourceRequestsQueryVariables?.requestAccessLevel,
              planFilter: resourcePlanFilter,
              chartSelectedTab: resourcePlanTabVariables?.resourcingPlanTab
            })
          ]
        : []),
      ...(isHiringStatusSubFilterCacheUpdateEnabled
        ? [
            updateHiringStatusSummaryCountCache({
              responseType: 'markResourceRequestAsRejected2',
              newRequestStatus: ResourceRequestStatus.Rejected,
              oldHiringStatus: request.sourceMetadata?.hiringStatus,
              existingRequestStatus: request.requestStatus,
              hiringStatusSummaryCountQueryVariables
            })
          ]
        : [])
    ],
    onResourceRequestUnsubmitCacheFunctions: [
      draftUpdateCache(null, request.projectUri),
      ...(isProjectResourcingView
        ? [
            updateResourcePlanSummaryTabCountCache({
              responseType: 'markResourceRequestAsDraft2',
              newRequestStatus: ResourceRequestStatus.Draft,
              resourceRequest: request,
              planFilter: summaryCountPlanFilter
            }),
            updateResourcePlansCache({
              existingResourceRequest: request,
              newRequestStatus: ResourceRequestStatus.Draft,
              requestAccessLevel:
                resourceRequestsQueryVariables?.requestAccessLevel,
              planFilter: resourcePlanFilter,
              chartSelectedTab: resourcePlanTabVariables?.resourcingPlanTab
            })
          ]
        : []),
      ...(isHiringStatusSubFilterCacheUpdateEnabled
        ? [
            updateHiringStatusSummaryCountCache({
              responseType: 'markResourceRequestAsDraft2',
              newRequestStatus: ResourceRequestStatus.Draft,
              oldHiringStatus: request.sourceMetadata?.hiringStatus,
              existingRequestStatus: request.requestStatus,
              hiringStatusSummaryCountQueryVariables
            })
          ]
        : [])
    ],
    onResourceRequestAcceptCacheFunctions: [
      onResourceAcceptCacheUpdate(),
      ...(isProjectResourcingView
        ? [
            updateResourcePlanSummaryTabCountCache({
              responseType: 'markResourceRequestAsComplete2',
              newRequestStatus: ResourceRequestStatus.Complete,
              resourceRequest: request,
              planFilter: summaryCountPlanFilter
            }),
            updateResourcePlansCache({
              existingResourceRequest: request,
              newRequestStatus: ResourceRequestStatus.Complete,
              requestAccessLevel:
                resourceRequestsQueryVariables?.requestAccessLevel,
              planFilter: resourcePlanFilter,
              chartSelectedTab: resourcePlanTabVariables?.resourcingPlanTab
            })
          ]
        : [])
    ],
    onResourceRequestDeleteCacheFunctions: [
      updateDeleteCache({
        projectUri: request.projectUri,
        resourceRequestsQueryVariables: resourceRequestsQueryVariables
          ? {
              ...resourceRequestsQueryVariables,
              projectUri: request.projectUri
            }
          : null,
        request,
        filter,
        isResourceActualModeEnabled,
        skipProjectTotalsCacheUpdate
      }),
      ...(isProjectResourcingView
        ? [
            updateResourcePlanSummaryTabCountCache({
              responseType: 'deleteResourceRequest2',
              newRequestStatus: null,
              resourceRequest: request,
              planFilter: summaryCountPlanFilter
            }),
            updateResourcePlansCache({
              existingResourceRequest: request,
              newRequestStatus: null,
              requestAccessLevel:
                resourceRequestsQueryVariables?.requestAccessLevel,
              planFilter: resourcePlanFilter,
              chartSelectedTab: resourcePlanTabVariables?.resourcingPlanTab
            })
          ]
        : []),
      ...(isHiringStatusSubFilterCacheUpdateEnabled
        ? [
            updateHiringStatusSummaryCountCache({
              responseType: 'deleteResourceRequest2',
              newRequestStatus: null,
              oldHiringStatus: request.sourceMetadata?.hiringStatus,
              existingRequestStatus: request.requestStatus,
              hiringStatusSummaryCountQueryVariables
            })
          ]
        : [])
    ],
    onResourceRequestToBeHiredCacheFunctions: [
      ...(isProjectResourcingView
        ? [
            updateResourcePlanSummaryTabCountCache({
              responseType: 'markResourceRequestAsToBeHired2',
              newRequestStatus: ResourceRequestStatus.Tobehired,
              resourceRequest: request,
              planFilter: summaryCountPlanFilter
            }),
            updateResourcePlansCache({
              existingResourceRequest: request,
              newRequestStatus: ResourceRequestStatus.Tobehired,
              requestAccessLevel:
                resourceRequestsQueryVariables?.requestAccessLevel,
              planFilter: resourcePlanFilter,
              chartSelectedTab: resourcePlanTabVariables?.resourcingPlanTab
            }),
            ...(isPtiEnabled
              ? [
                  updateHiringStatusSummaryCountCache({
                    responseType: 'markResourceRequestAsToBeHired2',
                    newRequestStatus: ResourceRequestStatus.Tobehired,
                    oldHiringStatus: request.sourceMetadata?.hiringStatus,
                    existingRequestStatus: request.requestStatus,
                    hiringStatusSummaryCountQueryVariables
                  })
                ]
              : [])
          ]
        : [])
    ],
    onResourceRequestUpdateHiringStatusCacheFunctions: isHiringStatusSubFilterCacheUpdateEnabled
      ? [
          updateCacheForResourcingPlan({
            resourcePlanSubstatusFilter:
              resourcePlanTabVariables?.resourcingPlanSelectedSubstatus,
            resourceRequestsQueryVariables
          }),
          updateHiringStatusSummaryCountCache({
            responseType:
              'markToBeHiredResourceRequestWithHiringRequestedSubStatus',
            newRequestStatus: ResourceRequestStatus.Tobehired,
            oldHiringStatus: request.sourceMetadata?.hiringStatus,
            existingRequestStatus: request.requestStatus,
            hiringStatusSummaryCountQueryVariables
          })
        ]
      : []
  };
};
